import React from 'react';
import { useLanguage } from '../../hooks/useLanguage';

const ModuleDetailCard = ({ moduleName, data, loading }) => {
    const { t } = useLanguage();
    if (loading) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body placeholder-glow">
                    <h5 className="card-title placeholder col-8" aria-label={t('metrics.loading')}></h5>
                    <div className="placeholder col-12 mb-2"></div>
                    <div className="placeholder col-10 mb-2"></div>
                    <div className="placeholder col-11 mb-2"></div>
                    <div className="placeholder col-9"></div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="card shadow-sm h-100">
                <div className="card-body">
                    <h5 className="card-title">{moduleName}</h5>
                    <div className="text-center text-muted py-4">
                        <p>{t('metrics.noData')}</p>
                    </div>
                </div>
            </div>
        );
    }

    const getModuleConfig = (name) => {
        switch(name) {
            case 'Policy Navigator':
                return { color: 'primary', icon: 'bi-file-earmark-text-fill' };
            case 'Journal Finder':
                return { color: 'success', icon: 'bi-search' };
            case 'Concursos':
                return { color: 'warning', icon: 'bi-trophy-fill' };
            default:
                return { color: 'secondary', icon: 'bi-question-circle' };
        }
    };

    const { color: cardColor, icon } = getModuleConfig(moduleName);

    const getTendenciaDisplay = (tendencia) => {
        if (tendencia === null || tendencia === undefined) {
            return <span className="badge bg-secondary">{t('metrics.moduleDetail.noData')}</span>;
        }
        if (tendencia > 0) {
            return (
                <span className="badge bg-success">
                    <i className="bi bi-arrow-up"></i> +{tendencia.toFixed(1)}%
                </span>
            );
        } else if (tendencia < 0) {
            return (
                <span className="badge bg-danger">
                    <i className="bi bi-arrow-down"></i> {tendencia.toFixed(1)}%
                </span>
            );
        } else {
            return <span className="badge bg-secondary">{t('metrics.moduleDetail.noChanges')}</span>;
        }
    };

    return (
        <div className="card shadow-sm h-100">
            <div className={`card-header bg-${cardColor} text-white`}>
                <h5 className="card-title mb-0">
                    <i className={`bi ${icon} me-2`}></i>
                    {moduleName}
                </h5>
            </div>
            <div className="card-body">
                {/* Estadísticas principales */}
                <div className="row g-3 mb-4">
                    <div className="col-6">
                        <div className="border-start border-3 border-primary ps-3">
                            <div className="text-muted small">{t('metrics.moduleDetail.totalQueries')}</div>
                            <div className="fs-4 fw-bold">{data.total_consultas.toLocaleString()}</div>
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="border-start border-3 border-success ps-3">
                            <div className="text-muted small">{t('metrics.moduleDetail.uniqueUsers')}</div>
                            <div className="fs-4 fw-bold">{data.usuarios_unicos}</div>
                        </div>
                    </div>
                </div>

                {/* Promedio y tendencia */}
                <div className="mb-3 p-3 bg-light rounded">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted small">{t('metrics.moduleDetail.dailyAverage')}</span>
                        <span className="fw-bold">{data.promedio_dia.toFixed(1)} {t('metrics.moduleDetail.queriesPerDay')}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small">{t('metrics.moduleDetail.trend')}</span>
                        {getTendenciaDisplay(data.tendencia_pct)}
                    </div>
                </div>

                {/* Fechas */}
                <div className="border-top pt-3">
                    <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted small">
                            <i className="bi bi-calendar-check me-1"></i>
                            {t('metrics.moduleDetail.firstQuery')}
                        </span>
                        <span className="small">{data.primera_consulta || '-'}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                        <span className="text-muted small">
                            <i className="bi bi-clock me-1"></i>
                            {t('metrics.moduleDetail.lastQuery')}
                        </span>
                        <span className="small">{data.ultima_consulta || '-'}</span>
                    </div>
                </div>

                {/* Badge de estado */}
                <div className="mt-3 text-center">
                    {data.total_consultas > 100 ? (
                        <span className="badge bg-success">
                            <i className="bi bi-fire me-1"></i>
                            {t('metrics.moduleDetail.status.veryActive')}
                        </span>
                    ) : data.total_consultas > 50 ? (
                        <span className="badge bg-info">
                            <i className="bi bi-graph-up me-1"></i>
                            {t('metrics.moduleDetail.status.active')}
                        </span>
                    ) : data.total_consultas > 0 ? (
                        <span className="badge bg-warning">
                            <i className="bi bi-hourglass-split me-1"></i>
                            {t('metrics.moduleDetail.status.growing')}
                        </span>
                    ) : (
                        <span className="badge bg-secondary">
                            <i className="bi bi-pause-circle me-1"></i>
                            {t('metrics.moduleDetail.status.inactive')}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ModuleDetailCard;
